import { useState, useEffect, useCallback } from "react";
import { Copy, Download, Loader2 } from "lucide-react";
import { Shimmer, TableShimmer } from "@/components/ui/shimmer";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTablePagination } from "@/components/ui/data-table-pagination";
import TransactionFiltersPanel, {
  getDefaultFilters,
  type FilterValues,
} from "@/components/common/TransactionFiltersPanel";
import { useAuth } from "@/context/AuthContext";
import {
  getBBPSOrders,
  getAllBBPSOrders,
  getBBPSOrderStatuses,
  type BBPSOrderRow,
  type BBPSOrderFilters,
} from "@/utils/bbps-orders-api";
import { exportBBPSOrdersToExcel } from "@/utils/export-excel";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const statusBadgeClass: Record<string, string> = {
  Done: "border-green-500 text-green-600 dark:text-green-400",
  Processing: "border-blue-500 text-blue-600 dark:text-blue-400",
  Refunded: "border-red-500 text-red-500 dark:text-red-400",
  Failed: "border-red-500 text-red-500 dark:text-red-400",
  Pending: "border-yellow-500 text-yellow-600 dark:text-yellow-400",
};

export default function BBPSOrdersPage() {
  const { userId, token } = useAuth();

  const [rows, setRows] = useState<BBPSOrderRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState<FilterValues>(getDefaultFilters);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);

  useEffect(() => {
    if (!userId || !token) return;
    getBBPSOrderStatuses(userId, token).then(setAvailableStatuses).catch(() => {});
  }, [userId, token]);

  const buildFilters = useCallback((): BBPSOrderFilters => {
    const f: BBPSOrderFilters = { userId: userId! };
    if (filters.selectedTypes.length > 0) f.statuses = filters.selectedTypes;
    if (filters.createdFrom) f.createdFrom = filters.createdFrom.startOf("day").toISOString();
    if (filters.createdTo) f.createdTo = filters.createdTo.endOf("day").toISOString();
    if (filters.search.trim()) f.search = filters.search.trim();
    return f;
  }, [userId, filters]);

  const fetchData = useCallback(async () => {
    if (!userId || !token) return;
    setLoading(true);
    setError("");
    try {
      const result = await getBBPSOrders(buildFilters(), rowsPerPage, page * rowsPerPage, token);
      setRows(result.rows);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load BBPS orders");
    } finally {
      setLoading(false);
    }
  }, [userId, token, page, rowsPerPage, buildFilters]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(0); }, [filters]);

  const handleExport = async () => {
    if (!userId || !token) return;
    setExporting(true);
    try {
      const allRows = await getAllBBPSOrders(buildFilters(), token);
      exportBBPSOrdersToExcel(allRows, "bbps-orders");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const copyText = (text: string) => { navigator.clipboard.writeText(text); };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold">
          BBPS &mdash; Order History
        </h1>
        <p className="text-muted-foreground">
          View all BBPS bill payment and recharge transactions.
        </p>
      </div>

      <Shimmer loading={loading} fallback={<TableShimmer cols={7} />}>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <div>
                <h3 className="text-base font-semibold">BBPS Orders</h3>
                <span className="text-sm text-muted-foreground">
                  {total.toLocaleString()} orders total
                </span>
              </div>
            </div>

            <TransactionFiltersPanel
              filters={filters}
              onChange={setFilters}
              availableTypes={availableStatuses}
              actions={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  disabled={exporting || total === 0}
                >
                  {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  {exporting ? "Exporting..." : "Export Excel"}
                </Button>
              }
            />

            {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">Order ID</TableHead>
                  <TableHead className="font-semibold">Operator</TableHead>
                  <TableHead className="font-semibold">CA Number</TableHead>
                  <TableHead className="font-semibold text-right">Amount</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Created At</TableHead>
                  <TableHead className="font-semibold">Updated At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12">
                      <span className="text-muted-foreground">No BBPS orders found</span>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((order) => (
                    <TableRow key={order.order_id}>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <span className="text-xs font-mono">
                            {order.order_id.slice(0, 8)}...
                          </span>
                          <Button variant="ghost" size="icon-xs" onClick={() => copyText(order.order_id)}>
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-7 w-7 rounded-md">
                            <AvatarImage src={order.whatsub_bbps_operator?.image} />
                            <AvatarFallback className="rounded-md">
                              {(order.whatsub_bbps_operator?.name ?? order.operator)[0]}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm font-medium max-w-[180px] truncate" title={order.whatsub_bbps_operator?.name ?? order.operator}>
                            {order.whatsub_bbps_operator?.name ?? order.operator}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-mono text-[0.8rem]">
                          {order.canumber}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-sm font-semibold">
                          ₹{Number(order.amount).toLocaleString("en-IN")}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={statusBadgeClass[order.status] ?? ""}
                        >
                          {order.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(order.created_at)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(order.updated_at)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <DataTablePagination
              count={total}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(p) => setPage(p)}
              onRowsPerPageChange={(rpp) => { setRowsPerPage(rpp); setPage(0); }}
            />
          </CardContent>
        </Card>
      </Shimmer>
    </div>
  );
}
